// =======================================================
// 📄 src/LandLordPages/ForgetPassword.jsx
// =======================================================
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2,
  ArrowLeft,
  Eye,
  EyeOff,
  RefreshCw,
  KeyRound,
  ShieldCheck,
} from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  sendOtpForPasswordReset,
  verifyOtpForPasswordReset,
  landlordResetPassword,
} from "../api/LandlordDashboard";

// --- helpers ---
const extractErr = (err, fallback = "Something went wrong.") =>
  err?.response?.data?.detail ||
  err?.response?.data?.message ||
  err?.message ||
  fallback;

const pwChecks = [
  { id: "len8", label: "At least 8 chars", test: (v) => v.length >= 8 },
  { id: "lower", label: "Lowercase", test: (v) => /[a-z]/.test(v) },
  { id: "upper", label: "Uppercase", test: (v) => /[A-Z]/.test(v) },
  { id: "num", label: "Number", test: (v) => /\d/.test(v) },
  { id: "sym", label: "Symbol", test: (v) => /[^A-Za-z0-9]/.test(v) },
];

export default function ForgetPassword() {
  const [step, setStep] = useState("email"); // 'email' | 'otp' | 'reset'
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [showPw1, setShowPw1] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const inputsRef = useRef([]);
  const transition = { duration: 0.35, ease: "easeInOut" };

  // resend cooldown (30s)
  const [resendIn, setResendIn] = useState(0);
  const resendTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };
  }, []);

  const startResend = (sec = 30) => {
    setResendIn(sec);
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    resendTimerRef.current = setInterval(() => {
      setResendIn((s) => {
        if (s <= 1) {
          clearInterval(resendTimerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const handleBackToEmail = () => {
    setStep("email");
    setOtp(Array(6).fill(""));
    resetFormPassword();
    setStatus("");
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    setResendIn(0);
  };

  useEffect(() => {
    if (step !== "otp") setOtp(Array(6).fill(""));
  }, [step]);

  // --- Email form ---
  const emailForm = useFormik({
    initialValues: { email: "" },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required("Email is required"),
    }),
    onSubmit: async ({ email }) => {
      setStatus("");
      setLoading(true);
      try {
        await sendOtpForPasswordReset(email);
        setStep("otp");
        setTimeout(() => inputsRef.current[0]?.focus(), 0);
        startResend(30); // << cooldown
      } catch (err) {
        setStatus(extractErr(err));
      } finally {
        setLoading(false);
      }
    },
  });

  // --- Reset form ---
  const passwordForm = useFormik({
    initialValues: { new_password: "", confirm_password: "" },
    validationSchema: Yup.object({
      new_password: Yup.string()
        .min(8, "Must be at least 8 characters")
        .required("New password is required"),
      confirm_password: Yup.string()
        .oneOf([Yup.ref("new_password")], "Passwords must match")
        .required("Confirm your password"),
    }),
    onSubmit: async ({ new_password }) => {
      setStatus("");
      setLoading(true);
      try {
        await landlordResetPassword({
          email: emailForm.values.email,
          otp: otp.join(""),
          new_password,
        });
        setStatus("✅ Password changed successfully. Redirecting...");
        setTimeout(() => (window.location.href = "/"), 1200);
      } catch (err) {
        setStatus(extractErr(err));
      } finally {
        setLoading(false);
      }
    },
  });
  const resetFormPassword = () =>
    passwordForm.setValues({ new_password: "", confirm_password: "" });

  // Strength meter like Settings
  const score = useMemo(
    () =>
      pwChecks.reduce(
        (acc, c) => acc + (c.test(passwordForm.values.new_password) ? 1 : 0),
        0
      ),
    [passwordForm.values.new_password]
  );
  const strength = score <= 2 ? "Weak" : score <= 4 ? "Medium" : "Strong";
  const strengthBar = Array.from({ length: 5 }, (_, i) => i < score);

  // OTP handlers
  function handleOtpChange(val, idx) {
    if (!/^[0-9]?$/.test(val)) return;
    const next = [...otp];
    next[idx] = val;
    setOtp(next);
    if (val && idx < 5) inputsRef.current[idx + 1]?.focus();
  }
  function handleOtpPaste(e) {
    const pasted = e.clipboardData?.getData("text")?.trim() || "";
    if (/^\d{6}$/.test(pasted)) {
      e.preventDefault();
      setOtp(pasted.split(""));
      inputsRef.current[5]?.blur();
    }
  }
  async function handleVerifyOtp() {
    const code = otp.join("");
    if (code.length !== 6) return setStatus("Enter 6 digit OTP.");
    setStatus("");
    setLoading(true);
    try {
      await verifyOtpForPasswordReset(emailForm.values.email, code);
      setStep("reset");
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
      setResendIn(0);
    } catch (err) {
      setStatus(extractErr(err));
    } finally {
      setLoading(false);
    }
  }
  function handleOtpKeyDown(e, idx) {
    if (e.key === "Backspace" && !otp[idx] && idx > 0)
      inputsRef.current[idx - 1]?.focus();
    if (e.key === "Enter" && otp.join("").length === 6 && !loading)
      handleVerifyOtp();
  }

  const handleResend = async () => {
    if (resendIn > 0 || !emailForm.values.email) return;
    setStatus("");
    setLoading(true);
    try {
      await sendOtpForPasswordReset(emailForm.values.email);
      startResend(30);
    } catch (err) {
      setStatus(extractErr(err, "Failed to resend code."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-300 via-white to-violet-300 p-4">
      {/* animated blobs */}
      <motion.div
        className="absolute -top-24 -left-20 w-[420px] h-[420px] bg-blue-400 rounded-full blur-3xl opacity-30"
        animate={{ x: [0, 20, 0], y: [0, 30, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute -bottom-24 -right-12 w-[380px] h-[380px] bg-fuchsia-400 rounded-full blur-3xl opacity-30"
        animate={{ x: [0, -20, 0], y: [0, -30, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={transition}
        className="relative w-full max-w-md bg-white/65 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/40 p-6 sm:p-8"
      >
        {step !== "email" && (
          <button
            onClick={handleBackToEmail}
            className="absolute top-4 left-4 text-gray-500 hover:text-gray-700 flex items-center gap-1 text-sm"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        )}

        <div className="flex items-center justify-center gap-2 mb-2">
          {step === "email" && (
            <ShieldCheck className="h-5 w-5 text-blue-600" />
          )}
          {step !== "email" && <KeyRound className="h-5 w-5 text-blue-600" />}
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">
            {step === "email" && "Forgot Password"}
            {step === "otp" && "Verify Email"}
            {step === "reset" && "Set New Password"}
          </h1>
        </div>
        <p className="text-sm text-gray-600 text-center mb-6">
          {step === "email" &&
            "Enter your registered email to receive a verification code."}
          {step === "otp" &&
            `Enter the 6-digit code we sent to ${emailForm.values.email}.`}
          {step === "reset" &&
            "Choose a strong new password to secure your account."}
        </p>

        {status && (
          <div
            className={`mb-4 text-center text-sm ${
              status.startsWith("✅")
                ? "text-green-700 bg-green-50 border-green-200"
                : "text-red-700 bg-red-50 border-red-200"
            } border rounded-lg py-2 px-3`}
          >
            {status}
          </div>
        )}

        <AnimatePresence mode="wait">
          {/* EMAIL */}
          {step === "email" && (
            <motion.form
              key="email-step"
              onSubmit={emailForm.handleSubmit}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={transition}
              className="space-y-3"
            >
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={emailForm.values.email}
                  onChange={emailForm.handleChange}
                  onBlur={emailForm.handleBlur}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm bg-white/85 focus:ring-2 focus:ring-blue-400 outline-none transition"
                />
                {emailForm.touched.email && emailForm.errors.email && (
                  <p className="mt-1 text-xs text-red-600">
                    {emailForm.errors.email}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium py-3 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition disabled:opacity-50 flex justify-center items-center gap-2"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Send Verification Code
              </button>
            </motion.form>
          )}

          {/* OTP */}
          {step === "otp" && (
            <motion.div
              key="otp-step"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={transition}
              className="space-y-6"
            >
              <div className="flex justify-center gap-2 sm:gap-3">
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => (inputsRef.current[idx] = el)}
                    type="text"
                    maxLength={1}
                    inputMode="numeric"
                    value={digit}
                    onChange={(e) => handleOtpChange(e.target.value, idx)}
                    onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                    onPaste={idx === 0 ? handleOtpPaste : undefined}
                    className="w-12 h-12 sm:w-14 sm:h-14 text-center text-lg font-semibold rounded-xl border border-gray-300 bg-white/85 focus:ring-2 focus:ring-blue-400 outline-none transition"
                  />
                ))}
              </div>

              <div className="flex items-center justify-between text-xs text-gray-600">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendIn > 0 || loading}
                  className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 hover:bg-gray-50 disabled:opacity-50"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                </button>
                <span>Didn’t get it? Check spam.</span>
              </div>

              <button
                onClick={handleVerifyOtp}
                disabled={loading || otp.join("").length !== 6}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium py-3 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition disabled:opacity-50 flex justify-center items-center gap-2"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Verify Code
              </button>
            </motion.div>
          )}

          {/* RESET */}
          {step === "reset" && (
            <motion.form
              key="reset-step"
              onSubmit={passwordForm.handleSubmit}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={transition}
              className="space-y-4"
            >
              <div className="relative">
                <input
                  type={showPw1 ? "text" : "password"}
                  name="new_password"
                  placeholder="New Password"
                  value={passwordForm.values.new_password}
                  onChange={passwordForm.handleChange}
                  onBlur={passwordForm.handleBlur}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 pr-10 text-sm bg-white/85 focus:ring-2 focus:ring-blue-400 outline-none transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPw1((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  aria-label={showPw1 ? "Hide password" : "Show password"}
                >
                  {showPw1 ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
                {passwordForm.touched.new_password &&
                  passwordForm.errors.new_password && (
                    <p className="mt-1 text-xs text-red-600">
                      {passwordForm.errors.new_password}
                    </p>
                  )}
              </div>

              {/* Strength meter */}
              <div>
                <div className="flex items-center gap-2">
                  {strengthBar.map((on, i) => (
                    <div
                      key={i}
                      className={`h-1.5 w-full rounded-full ${
                        on ? "bg-blue-600" : "bg-gray-200"
                      }`}
                    />
                  ))}
                </div>
                <div className="mt-1 text-[11px] text-gray-500">
                  Strength: <span className="font-medium">{strength}</span>
                </div>

                {/* ⬇️ paste the checklist block here */}
                <ul className="mt-2 grid grid-cols-2 gap-1 text-[11px] text-gray-600">
                  {pwChecks.map((c) => {
                    const ok = c.test(passwordForm.values.new_password);
                    return (
                      <li
                        key={c.id}
                        className={`inline-flex items-center gap-1 ${
                          ok ? "text-green-600" : "text-gray-500"
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            ok ? "bg-green-600" : "bg-gray-300"
                          }`}
                        />
                        {c.label}
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="relative">
                <input
                  type={showPw2 ? "text" : "password"}
                  name="confirm_password"
                  placeholder="Confirm Password"
                  value={passwordForm.values.confirm_password}
                  onChange={passwordForm.handleChange}
                  onBlur={passwordForm.handleBlur}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 pr-10 text-sm bg-white/85 focus:ring-2 focus:ring-blue-400 outline-none transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPw2((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  aria-label={showPw2 ? "Hide password" : "Show password"}
                >
                  {showPw2 ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
                {passwordForm.touched.confirm_password &&
                  passwordForm.errors.confirm_password && (
                    <p className="mt-1 text-xs text-red-600">
                      {passwordForm.errors.confirm_password}
                    </p>
                  )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium py-3 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition disabled:opacity-50 flex justify-center items-center gap-2"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Reset Password
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
